import React, { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { IInvestmentDetailField } from "store/types";

export type CsvColumnMapping = Record<string, string | null>;

interface CsvColumnMapperProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (mapping: CsvColumnMapping) => void;
  csvHeaders: string[];
  fields: IInvestmentDetailField[];
}

const FIXED_COLS = [
  { key: "date", label: "Date" },
  { key: "currency", label: "Currency" }
];

const autoMatch = (key: string, csvHeaders: string[]): string | null =>
  csvHeaders.find((h) => h.toLowerCase() === key.toLowerCase()) ?? null;

export const CsvColumnMapper: React.FC<CsvColumnMapperProps> = ({ open, onClose, onSubmit, csvHeaders, fields }) => {
  const [mapping, setMapping] = useState<CsvColumnMapping>({});

  const allCols = [...FIXED_COLS, ...fields.map((f) => ({ key: f.name, label: f.name }))];

  useEffect(() => {
    if (!open) return;
    const initial: CsvColumnMapping = {};
    allCols.forEach(({ key }) => {
      initial[key] = autoMatch(key, csvHeaders);
    });
    setMapping(initial);
  }, [open, csvHeaders, fields]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Map CSV Columns</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Match each required column to the corresponding column in your CSV file.
        </Typography>
        <Stack sx={{ mt: 1 }}>
          <Stack direction="row" sx={{ mb: 1, px: 1 }}>
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1, color: "text.secondary" }}>
              Expected Column
            </Typography>
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1, color: "text.secondary" }}>
              Mapped Column
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {allCols.map(({ key, label }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={2}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {label}
                </Typography>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={mapping[key] ?? ""}
                    displayEmpty
                    onChange={(e) => setMapping((prev) => ({ ...prev, [key]: e.target.value || null }))}
                  >
                    <MenuItem value="">
                      <em>Not mapped</em>
                    </MenuItem>
                    {csvHeaders.map((header) => (
                      <MenuItem key={header} value={header}>
                        {header}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(mapping)}>
          Import
        </Button>
      </DialogActions>
    </Dialog>
  );
};
